import { apiService } from "../api.service";
import {
	type ConfigurationValue,
	ConfigurationValuesArraySchema,
	type GetConfigurationValuesResponse,
	UserConfigApiError,
} from "./user-config.types";

/**
 * User Configuration Service
 *
 * Handles all user configuration-related API operations including:
 * - Fetching all configuration values for a user
 * - Setting/updating a configuration value
 * - Deleting a configuration value
 */
export class UserConfigService {
	private readonly baseUrl = "/users";

	// ==================== CORE OPERATIONS ====================

	/**
	 * Get all configuration values for a user
	 * GET /users/{userId}/configuration-values
	 *
	 * @param userId - The user's UUID
	 * @returns Array of configuration values
	 * @throws UserConfigApiError if not found or API error
	 */
	async getConfigurationValues(
		userId: string,
	): Promise<GetConfigurationValuesResponse> {
		try {
			const data = await apiService.get<ConfigurationValue[]>(
				`${this.baseUrl}/${userId}/configuration-values`,
			);
			return ConfigurationValuesArraySchema.parse(data);
		} catch (error) {
			if (error instanceof UserConfigApiError) {
				throw error;
			}
			throw new UserConfigApiError(
				`Failed to get configuration values for user "${userId}": ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Set or update a configuration value for a user
	 * POST /users/{userId}/configuration-values/{key}
	 *
	 * The API expects the request body to be a JSON string literal.
	 * Since the value is already a string (e.g., JSON-stringified object),
	 * we need to JSON.stringify it again so it becomes a proper JSON string.
	 *
	 * Example: value = '[{"id":"x"}]' becomes body = '"[{\"id\":\"x\"}]"'
	 *
	 * @param userId - The user's UUID
	 * @param key - The configuration key
	 * @param value - The configuration value (will be JSON-encoded as a string literal)
	 * @returns void
	 * @throws UserConfigApiError if validation fails or API error
	 */
	async setConfigurationValue(
		userId: string,
		key: string,
		value: string,
	): Promise<void> {
		try {
			// The API expects a JSON string in the body, so we need to stringify
			// the value again. If value is '[{"id":"x"}]', the body becomes '"[{\"id\":\"x\"}]"'
			await apiService.post(
				`${this.baseUrl}/${userId}/configuration-values/${encodeURIComponent(key)}`,
				JSON.stringify(value),
				{
					headers: {
						"Content-Type": "application/json",
					},
				},
			);
		} catch (error) {
			if (error instanceof UserConfigApiError) {
				throw error;
			}
			throw new UserConfigApiError(
				`Failed to set configuration value "${key}" for user "${userId}": ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete a configuration value for a user
	 * DELETE /users/{userId}/configuration-values/{key}
	 *
	 * @param userId - The user's UUID
	 * @param key - The configuration key to delete
	 * @returns void
	 * @throws UserConfigApiError if not found or API error
	 */
	async deleteConfigurationValue(userId: string, key: string): Promise<void> {
		try {
			await apiService.delete(
				`${this.baseUrl}/${userId}/configuration-values/${encodeURIComponent(key)}`,
			);
		} catch (error) {
			if (error instanceof UserConfigApiError) {
				throw error;
			}
			throw new UserConfigApiError(
				`Failed to delete configuration value "${key}" for user "${userId}": ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== UTILITY METHODS ====================

	/**
	 * Get configuration values filtered by key prefix
	 *
	 * @param userId - The user's UUID
	 * @param prefix - The key prefix to filter by (e.g., "admin.organizationUsers")
	 * @returns Array of configuration values matching the prefix
	 */
	async getConfigurationValuesByPrefix(
		userId: string,
		prefix: string,
	): Promise<ConfigurationValue[]> {
		const allConfigs = await this.getConfigurationValues(userId);
		return allConfigs.filter((config) => config.key?.startsWith(prefix));
	}

	/**
	 * Get a single configuration value by key
	 *
	 * @param userId - The user's UUID
	 * @param key - The exact configuration key
	 * @returns The configuration value or null if not found
	 */
	async getConfigurationValueByKey(
		userId: string,
		key: string,
	): Promise<ConfigurationValue | null> {
		const allConfigs = await this.getConfigurationValues(userId);
		return allConfigs.find((config) => config.key === key) ?? null;
	}

	/**
	 * Set multiple configuration values at once
	 *
	 * @param userId - The user's UUID
	 * @param configs - Array of { key, value } pairs to set
	 * @returns void
	 */
	async setConfigurationValues(
		userId: string,
		configs: Array<{ key: string; value: string }>,
	): Promise<void> {
		await Promise.all(
			configs.map(({ key, value }) =>
				this.setConfigurationValue(userId, key, value),
			),
		);
	}
}

/**
 * Singleton instance of UserConfigService
 * Use this exported instance throughout the application
 */
export const userConfigService = new UserConfigService();
